import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiShield, FiArrowLeft, FiEdit, FiDownload, 
  FiPlus, FiTrash2, FiUser, FiAlertTriangle, FiDollarSign, FiCheckCircle, FiClock 
} from 'react-icons/fi';
import { litigationAPI } from '../../services/api';
import { toast } from 'react-toastify';
import LitigationFormModal from './LitigationFormModal';

const LEGAL_PHASES = [
  { key: 'amicable', label: '1. Amiable' },
  { key: 'formal_notice', label: '2. Mise en Demeure (30j)' },
  { key: 'bailiff_commandment', label: '3. Huissier' },
  { key: 'court_referral', label: '4. Tribunal' },
  { key: 'court_hearing', label: '5. Audience' },
  { key: 'judgment_rendered', label: '6. Jugement' },
  { key: 'enforcement_eviction', label: '7. Expulsion' },
  { key: 'settled_closed', label: '8. Soldé / Clos' },
];

const LitigationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [litigation, setLitigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Modal Acte de procédure
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    event_type: 'mise_en_demeure',
    event_date: new Date().toISOString().split('T')[0],
    title: '',
    description: '',
    amount_paid: '',
    document_url: '',
  });

  // Modal Échéancier amiable
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleInstallments, setScheduleInstallments] = useState(3);
  const [firstDueDate, setFirstDueDate] = useState(new Date().toISOString().split('T')[0]);

  // Modal Paiement Échéance
  const [payingSchedule, setPayingSchedule] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const fetchLitigation = useCallback(async () => {
    try {
      const res = await litigationAPI.getById(id);
      setLitigation(res.data);
    } catch {
      toast.error('Erreur chargement du contentieux');
      navigate('/litigations');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchLitigation();
  }, [fetchLitigation]);

  // Calculs financiers stricts et sans double comptage
  const financialData = useMemo(() => {
    if (!litigation) return { totalClaimed: 0, recoveredAmount: 0, remainingBalance: 0, schedulesCount: 0, paidSchedulesCount: 0, totalScheduleDue: 0, totalSchedulePaid: 0, isScheduleFullyPaid: false };

    const totalClaimed = parseFloat(litigation.total_claimed_amount || 0);
    const schedules = litigation.payment_schedules || [];
    const events = litigation.events || [];

    const schedulesCount = schedules.length;
    const paidSchedulesCount = schedules.filter(s => s.status === 'paid' || parseFloat(s.amount_paid || 0) >= parseFloat(s.amount_due || 0)).length;
    const totalScheduleDue = schedules.reduce((sum, s) => sum + parseFloat(s.amount_due || 0), 0);
    const totalSchedulePaid = schedules.reduce((sum, s) => sum + parseFloat(s.amount_paid || 0), 0);

    // Paiements directs hors échéancier
    const directEventsPaid = events
      .filter(e => parseFloat(e.amount_paid || 0) > 0 && !e.title?.startsWith('Règlement échéance'))
      .reduce((sum, e) => sum + parseFloat(e.amount_paid || 0), 0);

    // Montant recouvré exact et autodéterminé
    const recoveredAmount = schedulesCount > 0 ? (totalSchedulePaid + directEventsPaid) : parseFloat(litigation.recovered_amount || 0);
    const remainingBalance = Math.max(0, totalClaimed - recoveredAmount);
    const isScheduleFullyPaid = schedulesCount > 0 && paidSchedulesCount === schedulesCount;

    return {
      totalClaimed,
      recoveredAmount,
      remainingBalance,
      schedulesCount,
      paidSchedulesCount,
      totalScheduleDue,
      totalSchedulePaid,
      isScheduleFullyPaid
    };
  }, [litigation]);

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const res = await litigationAPI.downloadFormalNoticePDF(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mise-En-Demeure-${litigation.case_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Mise en demeure officielle (Art. 35 Loi 2016-037) téléchargée');
    } catch {
      toast.error('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleUpdatePhase = async (newPhase) => {
    try {
      await litigationAPI.update(id, { phase: newPhase });
      toast.success(`Phase de procédure mise à jour : ${newPhase}`);
      fetchLitigation();
    } catch (err) {
      toast.error('Erreur mise à jour de la phase');
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.event_date) return toast.warn('Champs obligatoires manquants');
    try {
      await litigationAPI.addEvent(id, eventForm);
      toast.success('Acte de procédure consigné avec succès !');
      setIsEventModalOpen(false);
      setEventForm({
        event_type: 'mise_en_demeure',
        event_date: new Date().toISOString().split('T')[0],
        title: '',
        description: '',
        amount_paid: '',
        document_url: '',
      });
      fetchLitigation();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet acte de procédure ?')) return;
    try {
      await litigationAPI.deleteEvent(id, eventId);
      toast.success('Acte supprimé');
      fetchLitigation();
    } catch {
      toast.error('Erreur suppression acte');
    }
  };

  const handleGenerateSchedules = async () => {
    const remaining = financialData.remainingBalance;
    if (remaining <= 0) return toast.warn('Aucun solde restant à échelonner');

    const num = parseInt(scheduleInstallments, 10) || 3;
    const installmentAmount = Math.round(remaining / num);
    const schedules = [];

    let currentD = new Date(firstDueDate);
    for (let i = 0; i < num; i++) {
      const dueDateStr = currentD.toISOString().split('T')[0];
      const amount = (i === num - 1) ? (remaining - installmentAmount * (num - 1)) : installmentAmount;
      schedules.push({
        due_date: dueDateStr,
        amount_due: amount,
        status: 'pending',
      });
      currentD.setMonth(currentD.getMonth() + 1);
    }

    try {
      await litigationAPI.savePaymentSchedules(id, { schedules });
      toast.success(`Échéancier de ${num} mensualités créé avec succès !`);
      setIsScheduleModalOpen(false);
      fetchLitigation();
    } catch {
      toast.error('Erreur génération de l\'échéancier');
    }
  };

  const handlePaySchedule = async (e) => {
    e.preventDefault();
    const parsedAmount = parseFloat(paymentAmount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) return toast.warn('Montant invalide');
    if (!payingSchedule) return;

    setSubmittingPayment(true);
    try {
      await litigationAPI.recordSchedulePayment(id, payingSchedule.id, {
        amount: parsedAmount,
        payment_reference: paymentRef,
      });
      toast.success(`Paiement de ${parsedAmount.toLocaleString('fr-FR')} XOF enregistré avec succès !`);
      setPayingSchedule(null);
      setPaymentAmount('');
      setPaymentRef('');
      fetchLitigation();
    } catch {
      toast.error('Erreur enregistrement paiement');
    } finally {
      setSubmittingPayment(false);
    }
  };

  if (loading || !litigation) {
    return <div style={{ padding: 60, textAlign: 'center' }}><div className="spinner" /></div>;
  }

  const currentPhaseIdx = LEGAL_PHASES.findIndex(p => p.key === litigation.phase);
  const { totalClaimed, recoveredAmount, remainingBalance, schedulesCount, paidSchedulesCount, totalScheduleDue, totalSchedulePaid, isScheduleFullyPaid } = financialData;
  const schedules = litigation.payment_schedules || [];

  return (
    <div className="page-container" style={{ paddingBottom: 40 }}>
      {/* ── Top Bar ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/litigations')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiArrowLeft /> Retour
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
                Litige #{litigation.case_number}
              </h1>
              <span style={{
                padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                background: '#fee2e2', color: '#991b1b',
              }}>
                {litigation.dispute_type.replace('_', ' ').toUpperCase()}
              </span>
              <span className={`badge badge-${litigation.status === 'resolved' || litigation.status === 'closed' ? 'success' : (litigation.status === 'in_progress' ? 'warning' : 'secondary')}`} style={{ fontSize: 11, fontWeight: 700 }}>
                {litigation.status === 'resolved' ? '✅ RÉSOUDRE / SOLDÉ' : (litigation.status === 'closed' ? '🔒 DOSSIER CLOS' : (litigation.status === 'in_progress' ? '⏳ EN COURS' : '📂 OUVERT'))}
              </span>
            </div>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              Preneur : <strong>{litigation.tenant_first_name} {litigation.tenant_last_name}</strong> · Bien : {litigation.property_title}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button 
            className="btn btn-outline btn-sm" 
            onClick={handleDownloadPDF} 
            disabled={downloadingPDF} 
            style={{ display: 'flex', alignItems: 'center', gap: 6, borderColor: '#b91c1c', color: '#b91c1c' }}
          >
            <FiDownload /> {downloadingPDF ? 'Génération...' : 'Mise en Demeure (Loi 2016-037 PDF)'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setIsEditModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiEdit /> Modifier le dossier
          </button>
        </div>
      </div>

      {/* ── Legal Alert Banner (Malian Law) ── */}
      {litigation.phase === 'formal_notice' && litigation.formal_notice_deadline && (
        <div style={{
          padding: '14px 18px', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12
        }}>
          <FiAlertTriangle size={24} color="#dc2626" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 700, color: '#991b1b', fontSize: 13 }}>
              DÉLAI LÉGAL IMPÉRATIF DE 30 JOURS EN COURS (ARTICLE 35 DE LA LOI N° 2016-037 DU MALI)
            </div>
            <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 2 }}>
              Mise en demeure envoyée le {new Date(litigation.formal_notice_sent_date).toLocaleDateString('fr-FR')}. Date limite de régularisation : <strong>{new Date(litigation.formal_notice_deadline).toLocaleDateString('fr-FR')}</strong>. À défaut de règlement, saisine du Juge des Référés pour expulsion immédiate.
            </div>
          </div>
        </div>
      )}

      {/* ── Procedure Progress ── */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 24, borderRadius: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>
          ÉTAPES DE LA PROCÉDURE CONTENTIEUSE SELON LE DROIT MALIEN :
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', overflowX: 'auto', paddingBottom: 6 }}>
          {LEGAL_PHASES.map((phase, idx) => {
            const isCompleted = idx <= currentPhaseIdx;
            const isCurrent = idx === currentPhaseIdx;
            return (
              <div 
                key={phase.key} 
                onClick={() => handleUpdatePhase(phase.key)}
                style={{ 
                  textAlign: 'center', flex: 1, minWidth: 105, cursor: 'pointer',
                  opacity: isCompleted ? 1 : 0.4,
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', margin: '0 auto 6px',
                  background: isCurrent ? '#dc2626' : (isCompleted ? '#16a34a' : '#cbd5e1'),
                  color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 12, boxShadow: isCurrent ? '0 0 0 4px #fee2e2' : 'none',
                }}>
                  {isCompleted && !isCurrent ? '✓' : idx + 1}
                </div>
                <div style={{ fontSize: 11, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? '#dc2626' : 'inherit' }}>
                  {phase.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Main Details Grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 24 }}>
        {/* Créance & Décompte */}
        <div className="card" style={{ padding: '20px', borderRadius: 10 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiDollarSign color="#dc2626" /> Décompte de la Créance (XOF)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Principal Loyers & Charges :</span>
              <strong>{parseFloat(litigation.unpaid_amount || 0).toLocaleString('fr-FR')} XOF</strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Pénalités & Intérêts :</span>
              <span>{parseFloat(litigation.penalty_amount || 0).toLocaleString('fr-FR')} XOF</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Frais d'Huissier de Justice :</span>
              <span>{parseFloat(litigation.bailiff_fees || 0).toLocaleString('fr-FR')} XOF</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Honoraires Avocat & Dépens :</span>
              <span>{(parseFloat(litigation.lawyer_fees || 0) + parseFloat(litigation.court_fees || 0)).toLocaleString('fr-FR')} XOF</span>
            </div>
            <div style={{ borderTop: '2px solid #fee2e2', paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: '#991b1b' }}>TOTAL RÉCLAMÉ :</span>
              <strong style={{ fontSize: 16, color: '#dc2626' }}>{totalClaimed.toLocaleString('fr-FR')} XOF</strong>
            </div>
            <div style={{ background: '#f0fdf4', padding: '8px 10px', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#166534', fontWeight: 600 }}>Montant Déjà Recouvré :</span>
              <strong style={{ color: '#16a34a', fontSize: 15 }}>{recoveredAmount.toLocaleString('fr-FR')} XOF</strong>
            </div>
            <div style={{ background: remainingBalance > 0 ? '#fef2f2' : '#f0fdf4', padding: '8px 10px', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: remainingBalance > 0 ? '#991b1b' : '#166534', fontWeight: 600 }}>
                {remainingBalance > 0 ? 'SOLDE RESTANT DÛ :' : 'CRÉANCE SOLDÉE :'}
              </span>
              <strong style={{ color: remainingBalance > 0 ? '#dc2626' : '#16a34a', fontSize: 15 }}>
                {remainingBalance.toLocaleString('fr-FR')} XOF
              </strong>
            </div>
          </div>
        </div>

        {/* Locataire & Bail */}
        <div className="card" style={{ padding: '20px', borderRadius: 10 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiUser color="#2563eb" /> Locataire & Contrat de Bail
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Locataire :</span>
              <strong>{litigation.tenant_first_name} {litigation.tenant_last_name}</strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Téléphone :</span>
              <span>{litigation.tenant_phone || 'Non renseigné'}</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Email :</span>
              <span>{litigation.tenant_email || 'Non renseigné'}</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Bien Occupé :</span>
              <span>{litigation.property_title}</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Adresse :</span>
              <span>{litigation.property_address}, {litigation.property_city || 'Bamako'}</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>N° Contrat :</span>
              <strong style={{ fontFamily: 'monospace', color: '#2563eb' }}>{litigation.contract_number || 'Bail direct'}</strong>
            </div>
          </div>
        </div>

        {/* Auxiliaires & Tribunal */}
        <div className="card" style={{ padding: '20px', borderRadius: 10 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiShield color="#7c3aed" /> Auxiliaires de Justice & Juridiction
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Huissier Mandaté :</span>
              <strong>{litigation.bailiff_name || 'À désigner'}</strong>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Contact Huissier :</span>
              <span>{litigation.bailiff_phone || '-'} {litigation.bailiff_email ? `· ${litigation.bailiff_email}` : ''}</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Avocat :</span>
              <span>{litigation.lawyer_name || 'À mandater'}</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Tribunal Compétent :</span>
              <span>{litigation.court_name || 'Tribunal d\'Instance de Bamako'}</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Audience :</span>
              <span>{litigation.court_hearing_date ? new Date(litigation.court_hearing_date).toLocaleDateString('fr-FR') : 'Non programmée'}</span>
            </div>
            <div style={detailRowStyle}>
              <span style={detailLabelStyle}>Jugement / Référé :</span>
              <span>{litigation.judgment_reference || (litigation.judgment_date ? new Date(litigation.judgment_date).toLocaleDateString('fr-FR') : 'En attente')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Protocole d'Échéancier de Paiement Amiable ── */}
      <div className="card" style={{ padding: '20px', borderRadius: 10, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                📅 Protocole d'Accord & Échéancier de Paiement ({schedulesCount})
              </h3>
              {schedulesCount > 0 && (
                isScheduleFullyPaid ? (
                  <span className="badge badge-success" style={{ fontWeight: 700, fontSize: 11 }}>
                    <FiCheckCircle style={{ marginRight: 4 }} /> Échéancier intégralement soldé ({paidSchedulesCount}/{schedulesCount})
                  </span>
                ) : (
                  <span className="badge badge-warning" style={{ fontWeight: 700, fontSize: 11 }}>
                    <FiClock style={{ marginRight: 4 }} /> {paidSchedulesCount} sur {schedulesCount} tranches réglées ({Math.round((totalSchedulePaid / (totalScheduleDue || 1)) * 100)}%)
                  </span>
                )
              )}
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
              Plan d'apurement amiable échelonné. Chaque tranche est déduite exactement une fois du solde restant.
            </p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => setIsScheduleModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiPlus /> Configurer l'Échéancier
          </button>
        </div>

        {schedulesCount === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg, #f8fafc)', borderRadius: 8, fontSize: 13 }}>
            Aucun échéancier de paiement amiable configuré. Cliquez sur "Configurer l'Échéancier" pour fractionner la créance.
          </div>
        ) : (
          <div className="table-container">
            <table style={{ width: '100%', minWidth: 650, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg, #f8fafc)', borderBottom: '2px solid var(--border, #e2e8f0)' }}>
                  <th style={thStyle}>Échéance</th>
                  <th style={thStyle}>Date d'Exigibilité</th>
                  <th style={thStyle}>Montant Dû</th>
                  <th style={thStyle}>Montant Payé</th>
                  <th style={thStyle}>Statut Tranche</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((sch, i) => {
                  const isPaid = sch.status === 'paid' || parseFloat(sch.amount_paid || 0) >= parseFloat(sch.amount_due || 0);
                  const remainingTranche = Math.max(0, parseFloat(sch.amount_due) - parseFloat(sch.amount_paid || 0));

                  return (
                    <tr key={sch.id} style={{ borderBottom: '1px solid var(--border, #e2e8f0)' }}>
                      <td style={tdStyle}>
                        <strong>Mensualité #{i + 1}</strong>
                        {sch.payment_reference && (
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                            Réf: <code>{sch.payment_reference}</code>
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>{new Date(sch.due_date).toLocaleDateString('fr-FR')}</td>
                      <td style={tdStyle}><strong>{parseFloat(sch.amount_due).toLocaleString('fr-FR')} XOF</strong></td>
                      <td style={tdStyle}>
                        <span style={{ color: sch.amount_paid > 0 ? '#16a34a' : '#64748b', fontWeight: sch.amount_paid > 0 ? 600 : 400 }}>
                          {parseFloat(sch.amount_paid || 0).toLocaleString('fr-FR')} XOF
                        </span>
                        {!isPaid && sch.amount_paid > 0 && (
                          <div style={{ fontSize: 11, color: '#c2410c' }}>
                            Reste: {remainingTranche.toLocaleString('fr-FR')} XOF
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '3px 9px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                          background: isPaid ? '#dcfce7' : (sch.amount_paid > 0 ? '#ffedd5' : '#f1f5f9'),
                          color: isPaid ? '#166534' : (sch.amount_paid > 0 ? '#c2410c' : '#475569'),
                        }}>
                          {isPaid ? '✅ Soldée' : (sch.amount_paid > 0 ? '⚠️ Partielle' : '⏳ En attente')}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {!isPaid && (
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                              setPayingSchedule(sch);
                              setPaymentAmount(String(remainingTranche));
                              setPaymentRef('');
                            }}
                            style={{ padding: '5px 12px', fontSize: 12, background: '#16a34a', borderColor: '#16a34a' }}
                          >
                            Encaisser
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Chronologie des Actes de Procédure ── */}
      <div className="card" style={{ padding: '20px', borderRadius: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              📜 Journal Chronologique des Actes de Procédure ({litigation.events?.length || 0})
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
              Historique opposable des sommations, exploits d'huissier, audiences et versements.
            </p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setIsEventModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiPlus /> Consigner un Acte / Paiement
          </button>
        </div>

        {(!litigation.events || litigation.events.length === 0) ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg, #f8fafc)', borderRadius: 8 }}>
            Aucun acte consigné.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {litigation.events.map(ev => (
              <div key={ev.id} style={{
                padding: '12px 16px', borderRadius: 8, border: '1px solid var(--border, #e2e8f0)',
                background: ev.event_type === 'paiement_partiel' ? '#f0fdf4' : '#ffffff',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: 10
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>
                      {new Date(ev.event_date).toLocaleDateString('fr-FR')}
                    </span>
                    <strong style={{ fontSize: 13, color: '#0f172a' }}>{ev.title}</strong>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                      background: '#ede9fe', color: '#7c3aed', textTransform: 'uppercase',
                    }}>
                      {ev.event_type.replace('_', ' ')}
                    </span>
                  </div>
                  {ev.description && (
                    <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                      {ev.description}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {parseFloat(ev.amount_paid || 0) > 0 && (
                    <strong style={{ color: '#16a34a', fontSize: 14 }}>
                      +{parseFloat(ev.amount_paid).toLocaleString('fr-FR')} XOF
                    </strong>
                  )}
                  {ev.document_url && (
                    <a href={ev.document_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline" style={{ padding: '4px 8px' }}>
                      <FiDownload size={13} /> Pièce
                    </a>
                  )}
                  <button onClick={() => handleDeleteEvent(ev.id)} style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', padding: 4 }}>
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Edition Litige */}
      <LitigationFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={fetchLitigation}
        litigationToEdit={litigation}
      />

      {/* Modal Nouvel Acte */}
      {isEventModalOpen && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal-content card" style={{ maxWidth: 500, width: '100%', padding: 20, borderRadius: 10 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Consigner un Acte de Procédure</h3>
            <form onSubmit={handleAddEvent}>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Nature de l'Acte / Événement *</label>
                <select 
                  className="form-control" 
                  value={eventForm.event_type} 
                  onChange={e => setEventForm({ ...eventForm, event_type: e.target.value })}
                >
                  <option value="mise_en_demeure">Mise en Demeure Formelle (30j)</option>
                  <option value="sommation_huissier">Commandement / Sommation d'Huissier</option>
                  <option value="assignation">Assignation en Référé Expulsion</option>
                  <option value="audience">Audience devant le Tribunal</option>
                  <option value="jugement">Ordonnance de Référé / Jugement d'Expulsion</option>
                  <option value="commandement_liberer">Commandement d'Avoir à Libérer les Lieux</option>
                  <option value="accord_amiable">Signature Protocole d'Accord Transactionnel</option>
                  <option value="paiement_partiel">Encaissement / Versement Partiel</option>
                  <option value="expulsion">Procès-Verbal d'Expulsion (Force Publique)</option>
                  <option value="note_interne">Note de Suivi Interne</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Date de l'Acte *</label>
                <input 
                  type="date" 
                  className="form-control" 
                  value={eventForm.event_date} 
                  onChange={e => setEventForm({ ...eventForm, event_date: e.target.value })} 
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Intitulé de l'Acte *</label>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Ex: Signification de l'ordonnance d'expulsion par Me ..."
                  value={eventForm.title} 
                  onChange={e => setEventForm({ ...eventForm, title: e.target.value })} 
                  required
                />
              </div>

              {eventForm.event_type === 'paiement_partiel' && (
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Montant Versé (XOF) *</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="0"
                    value={eventForm.amount_paid} 
                    onChange={e => setEventForm({ ...eventForm, amount_paid: e.target.value })} 
                    required
                  />
                </div>
              )}

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Description / Détails</label>
                <textarea 
                  rows={2}
                  className="form-control" 
                  value={eventForm.description} 
                  onChange={e => setEventForm({ ...eventForm, description: e.target.value })} 
                />
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Lien du Document / Exploit d'Huissier (URL)</label>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="https://..."
                  value={eventForm.document_url} 
                  onChange={e => setEventForm({ ...eventForm, document_url: e.target.value })} 
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-outline" onClick={() => setIsEventModalOpen(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer l'acte</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Configuration Échéancier */}
      {isScheduleModalOpen && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal-content card" style={{ maxWidth: 450, width: '100%', padding: 20, borderRadius: 10 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Configurer le Protocole d'Échéancier</h3>
            <div style={{ fontSize: 13, color: '#334155', marginBottom: 14 }}>
              Solde restant à apurer : <strong>{remainingBalance.toLocaleString('fr-FR')} XOF</strong>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Nombre de Mensualités :</label>
              <select className="form-control" value={scheduleInstallments} onChange={e => setScheduleInstallments(e.target.value)}>
                <option value="2">2 mensualités</option>
                <option value="3">3 mensualités</option>
                <option value="4">4 mensualités</option>
                <option value="6">6 mensualités</option>
                <option value="12">12 mensualités</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Date de 1ère Échéance :</label>
              <input type="date" className="form-control" value={firstDueDate} onChange={e => setFirstDueDate(e.target.value)} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-outline" onClick={() => setIsScheduleModalOpen(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleGenerateSchedules}>Générer l'échéancier</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Encaissement Échéance */}
      {payingSchedule && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal-content card" style={{ maxWidth: 420, width: '100%', padding: 20, borderRadius: 10 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Encaisser une Échéance Amiable</h3>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-secondary)' }}>
              Échéance du {new Date(payingSchedule.due_date).toLocaleDateString('fr-FR')} • Montant de la tranche : <strong>{parseFloat(payingSchedule.amount_due).toLocaleString('fr-FR')} XOF</strong>
            </p>
            <form onSubmit={handlePaySchedule}>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Montant Réglé (XOF) *</label>
                <input 
                  type="number" 
                  className="form-control" 
                  value={paymentAmount} 
                  onChange={e => setPaymentAmount(e.target.value)} 
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Référence Paiement / Reçu</label>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Ex: Espèces, Chèque N°..., Wave..."
                  value={paymentRef} 
                  onChange={e => setPaymentRef(e.target.value)} 
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-outline" onClick={() => setPayingSchedule(null)} disabled={submittingPayment}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={submittingPayment} style={{ background: '#16a34a', borderColor: '#16a34a' }}>
                  {submittingPayment ? 'Enregistrement...' : 'Valider le versement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: 'var(--text-secondary, #64748b)', textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};
const tdStyle = { padding: '12px 14px', fontSize: 13, verticalAlign: 'middle' };
const detailRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 6 };
const detailLabelStyle = { color: 'var(--text-secondary, #64748b)', fontSize: 12 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4, display: 'block' };
const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 16,
};

export default LitigationDetailPage;
